import { Controller } from "@hotwired/stimulus"

// Autosave strategy: poll every `delay` ms and compare a snapshot of all
// article[...] form fields against the last saved state. No dependency on
// event bubbling from custom elements (Trix) or data-action wiring.
//
// Values:
//   url   - PATCH /contributors/articles/:aid/autosave endpoint
//   delay - poll interval in ms (default 3000)

export default class extends Controller {
  static targets = ["status"]
  static values = {
    url:   String,
    delay: { type: Number, default: 3000 }
  }

  connect() {
    this.saving      = false
    this._lastSnap   = null

    // Wait one tick for Trix editors to finish initializing before
    // taking the baseline snapshot we'll diff against
    setTimeout(() => {
      this._lastSnap = this._snapshot()
    }, 500)

    // Poll on every interval; only sends a request when content changes
    this._intervalId = setInterval(() => this._poll(), this.delayValue)

    // Best-effort saves when the user switches tabs or closes the page
    this._boundHide    = this._saveOnHide.bind(this)
    this._boundUnload  = this._saveOnUnload.bind(this)
    document.addEventListener("visibilitychange", this._boundHide)
    window.addEventListener("beforeunload",       this._boundUnload)
  }

  disconnect() {
    clearInterval(this._intervalId)
    document.removeEventListener("visibilitychange", this._boundHide)
    window.removeEventListener("beforeunload",       this._boundUnload)
  }

  // ─── private ──────────────────────────────────────────────────────────────

  // Fields managed by background agents — never overwrite with stale form data.
  static AGENT_MANAGED_FIELDS = new Set(["article[excerpt]"])

  // Serialize all article[...] fields (excluding files and agent-managed fields)
  // into a stable string for cheap equality comparison.
  _snapshot() {
    const fd    = new FormData(this.element)
    const pairs = []
    for (const [key, val] of fd.entries()) {
      if (key.startsWith("article[") && !(val instanceof File) && !this.constructor.AGENT_MANAGED_FIELDS.has(key)) {
        pairs.push(key + "\x00" + val)
      }
    }
    return pairs.sort().join("\x01")
  }

  _poll() {
    if (this.saving || !this.urlValue) return

    const current = this._snapshot()

    // Still waiting for initial baseline — skip this tick
    if (this._lastSnap === null) return

    // Nothing changed
    if (current === this._lastSnap) return

    this._save(current)
  }

  async _save(snap) {
    if (this.saving || !this.urlValue) return
    this.saving = true
    this._showStatus("saving")

    const fd = new FormData(this.element)
    // Strip file inputs and agent-managed fields from autosave payload
    for (const [key, val] of [...fd.entries()]) {
      if (val instanceof File || this.constructor.AGENT_MANAGED_FIELDS.has(key)) fd.delete(key)
    }

    try {
      const response = await fetch(this.urlValue, {
        method:  "PATCH",
        body:    fd,
        headers: {
          "Accept":       "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      const data = await response.json()

      if (response.ok && data.saved) {
        this._lastSnap = snap || this._snapshot()
        this._showStatus("saved", data.saved_at)
        setTimeout(() => this._clearStatus(), 4000)
      } else {
        const msg = (data.errors || []).slice(0, 2).join(", ")
        this._showStatus("error", msg)
      }
    } catch (err) {
      this._showStatus("error")
    } finally {
      this.saving = false
    }
  }

  _saveOnHide() {
    if (document.visibilityState !== "hidden") return
    const current = this._snapshot()
    if (this._lastSnap !== null && current !== this._lastSnap) {
      this._save(current)
    }
  }

  _saveOnUnload() {
    if (this._lastSnap === null || !this.urlValue) return
    const current = this._snapshot()
    if (current === this._lastSnap) return

    const fd = new FormData(this.element)
    for (const [key, val] of [...fd.entries()]) {
      if (val instanceof File || this.constructor.AGENT_MANAGED_FIELDS.has(key)) fd.delete(key)
    }
    navigator.sendBeacon?.(this.urlValue, fd)
  }

  _showStatus(state, message) {
    if (!this.hasStatusTarget) return
    const el = this.statusTarget
    el.className = `autosave-status autosave-status--${state}`

    switch (state) {
      case "saving":
        el.innerHTML = `<span class="spinner-border spinner-border-sm me-1"
                              style="width:.7rem;height:.7rem;border-width:2px"
                              role="status" aria-hidden="true"></span>Saving\u2026`
        break
      case "saved":
        el.innerHTML = `<i class="bi bi-check2 me-1"></i>Saved${message ? " at " + message : ""}`
        break
      case "error":
        el.innerHTML = `<i class="bi bi-exclamation-triangle me-1"></i>Save failed${message ? ": " + message : ""}`
        break
    }
  }

  _clearStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.className = "autosave-status autosave-status--idle"
    this.statusTarget.textContent = ""
  }
}
